import { Typography } from "@material-ui/core";
import { Button, ContainerPage, TitlePage } from "components";
import { useTableParams } from "hooks/useTableParams";
import React, { useRef, useCallback } from "react";
import storageService from "services/storageService";
import { Table } from "shared";
import Cookies from "universal-cookie";
import { convertDate, reasonMap } from "utils";

import { Approve } from "./Approve";
import { Reject } from "./Reject";

const STATUS_FILTER = {
  label: "Status",
  parameter: "status",
  values: [
    { id: "pending", label: "Pendente" },
    { id: "approved", label: "Aprovado" },
    { id: "rejected", label: "Rejeitado" }
  ]
};

const mapStatus = status => {
  const state =
    {
      pending: "Pendente",
      approved: "Aprovado",
      rejected: "Rejeitado"
    }[status] || null;
  return state;
};

export default function CancellationApproval() {
  const cookies = new Cookies();
  const distributionCenterId = cookies.get("distributionCenterId");

  const approveRef = useRef(null);
  const rejectRef = useRef(null);

  const fetchApprovals = useCallback(
    async params => {
      if (!distributionCenterId) {
        return { data: { results: [], total_count: 0 } };
      }
      const { search, status, ...rest } = params;
      return storageService.getCancellationApprovals({
        distributionCenterId: Number(distributionCenterId),
        ...(status ? { status } : {}),
        ...(search ? { orderId: search } : {}),
        page: rest.pageIndex,
        pageSize: rest.pageSize,
        orderBy: rest.orderBy
      });
    },
    [distributionCenterId]
  );

  const { data, onChange, params, refresh, status, count, handleSearch } =
    useTableParams({
      service: fetchApprovals,
      paramsDefault: { status: "pending" }
    });

  const columns = [
    {
      field: "created",
      label: "Criado em",
      align: "center",
      disabledSort: true,
      style: { minWidth: "150px" },
      formatter: value => convertDate(new Date(value))
    },
    {
      field: "updated",
      label: "Atualizado em",
      align: "center",
      disabledSort: true,
      style: { minWidth: "160px" },
      formatter: value => convertDate(new Date(value))
    },
    {
      field: "status",
      label: "Status",
      align: "center",
      style: { minWidth: "120px" },
      formatter: value => mapStatus(value)
    },
    {
      field: "order_item",
      label: "Item",
      align: "center",
      style: { minWidth: "80px" }
    },
    {
      field: "product_base",
      label: "Produto",
      style: { minWidth: "160px" },
      formatter: value => value?.name ?? "—"
    },
    {
      field: "quantity",
      label: "Qtd.",
      align: "center",
      style: { minWidth: "100px" },
      formatter: value =>
        new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 4 }).format(
          value
        )
    },
    {
      field: "reason",
      label: "Motivo",
      align: "center",
      style: { minWidth: "160px" },
      renderCell: ({ data }) => {
        const { reason, order_id } = data;
        const orderInfoList = [2, 29, 30];
        const reasonValue = reasonMap(reason?.reason);

        return (
          <Typography style={{ fontSize: "0.9rem", color: "#414141" }}>
            {reasonValue}
            {orderInfoList.includes(reason?.id) && (
              <strong style={{ color: "#000000c7" }}>{` #${order_id}`}</strong>
            )}
          </Typography>
        );
      }
    },
    {
      field: "type",
      label: "Tipo",
      align: "center",
      style: { minWidth: "140px" },
      formatter: value => value?.name ?? "—"
    },
    {
      field: "to_be_accounted",
      label: "Contabilizar Em",
      align: "center",
      style: { minWidth: "150px" },
      formatter: value => (value ? convertDate(new Date(value)) : "—")
    },
    {
      field: "updated_by_name",
      label: "Aprovado por",
      align: "center",
      style: { minWidth: "160px" }
    },
    {
      field: "approve_action",
      label: "Aprovar",
      align: "center",
      style: { minWidth: "120px" },
      renderCell: ({ data: row }) =>
        row.status === "pending" ? (
          <Button
            variant="contained"
            size="small"
            onClick={() => approveRef.current.handleData(row)}
          >
            Aprovar
          </Button>
        ) : null
    },
    {
      field: "reject_action",
      label: "Rejeitar",
      align: "center",
      style: { minWidth: "120px" },
      renderCell: ({ data: row }) =>
        row.status === "pending" ? (
          <Button
            variant="contained"
            size="small"
            style={{ backgroundColor: "#f44336", color: "#fff" }}
            onClick={() => rejectRef.current.handleData(row)}
          >
            Rejeitar
          </Button>
        ) : null
    }
  ];

  return (
    <>
      <ContainerPage>
        <TitlePage>Cancelamentos</TitlePage>

        {!distributionCenterId && (
          <p style={{ marginBottom: 16, color: "#666" }}>
            Selecione um centro de distribuição para visualizar os
            cancelamentos.
          </p>
        )}

        <Table
          handleSearch={handleSearch}
          columns={columns}
          data={data}
          onChange={onChange}
          params={params}
          status={status}
          refresh={refresh}
          count={count}
          size="small"
          placeholder="ID do pedido"
          filterProps={STATUS_FILTER}
        />
      </ContainerPage>

      <Approve refresh={refresh} ref={approveRef} />
      <Reject refresh={refresh} ref={rejectRef} />
    </>
  );
}
